import { useAsync } from 'react-use';

import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

import {
  DAAS_APPLICATION_SET_TYPE,
  DAAS_APPLICATION_TYPE,
  DAAS_PROJECT_TYPE,
  DaaSType,
} from '../types';

/**
 * Maps a DaaS entity's `spec.type` to the `daas-backend` "list editable"
 * endpoint segment that authorizes it.
 */
const EDITABLE_LIST_PATH_BY_TYPE: Record<DaaSType, string> = {
  [DAAS_PROJECT_TYPE]: 'projects',
  [DAAS_APPLICATION_TYPE]: 'applications',
  [DAAS_APPLICATION_SET_TYPE]: 'application-sets',
};

const ALL_EDITABLE_LIST_PATHS = Object.values(EDITABLE_LIST_PATH_BY_TYPE);
const EMPTY_EDITABLE_REFS: Set<string> = new Set();

export interface UseDaaSEditableEntitiesOptions {
  /**
   * When `false`, the hook makes no backend call and returns an empty set. Use
   * this to keep the hook unconditionally called (per the rules of hooks) while
   * skipping the request when the editable set isn't needed.
   * @defaultValue true
   */
  enabled?: boolean;
}

/**
 * Hook that fetches the set of DaaS entity refs the current user may edit.
 *
 * @param entityType - optional DaaS `spec.type` to restrict the query to one kind
 * @param options - `enabled` (default `true`); set `false` to skip the call
 * @returns `editableRefs` – the set of editable entity refs, `loading` – whether
 *   the fetch is in progress, and `error` – any error raised while fetching.
 */
export const useDaaSEditableEntities = (
  entityType?: DaaSType,
  options?: UseDaaSEditableEntitiesOptions,
): {
  editableRefs: Set<string>;
  loading: boolean;
  error: Error | undefined;
} => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const enabled = options?.enabled ?? true;

  const { value, loading, error } = useAsync(async () => {
    if (!enabled) {
      return EMPTY_EDITABLE_REFS;
    }

    let paths: string[];
    if (entityType === undefined) {
      paths = ALL_EDITABLE_LIST_PATHS;
    } else {
      const path = EDITABLE_LIST_PATH_BY_TYPE[entityType];
      // Not a DaaS entity kind we can authorize - nothing editable, no call.
      if (!path) {
        return EMPTY_EDITABLE_REFS;
      }
      paths = [path];
    }

    const baseUrl = await discoveryApi.getBaseUrl('daas');
    const responses = await Promise.all(
      paths.map(p => fetchApi.fetch(`${baseUrl}/permissions/authorize/${p}`)),
    );

    const editableRefs = new Set<string>();
    for (const response of responses) {
      if (!response.ok) {
        throw new Error(
          `Failed to load DaaS editable entities: ${response.status} ${response.statusText}`,
        );
      }
      const data = (await response.json()) as { entityRefs?: string[] };
      for (const ref of data.entityRefs ?? []) {
        editableRefs.add(ref);
      }
    }
    return editableRefs;
  }, [entityType, enabled, discoveryApi, fetchApi]);

  return {
    editableRefs: value ?? EMPTY_EDITABLE_REFS,
    loading,
    error,
  };
};
